import { useCallback, useEffect, useState } from "react";
import { css } from "@emotion/css";
import { useDropzone } from "react-dropzone";
import { Flex } from "@components/Flex";
import { TextField } from "@design-system/TextField";
import { Button } from "@design-system/Button";
import { LoadingOverlay } from "@components/LoadingOverlay";
import { requestKontextImage } from "@remotes/requestKontextImage";
import { getTokens } from "@utils/authStorage";
import * as Sentry from "@sentry/react";
import { 
  trackGenerateAttempted, 
  trackGenerateSuccess, 
  trackGenerateFailed,
  trackImageUpload,
  trackCreditsDecremented
} from "@utils/analytics";



const ColorPage = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
  const [resultImageUrl, setResultImageUrl] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // 0 = original, 1 = result
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [showCreditsError, setShowCreditsError] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    const newFile = acceptedFiles[0];
    setFile(newFile);
    const url = URL.createObjectURL(newFile);
    
    // Track image upload event
    trackImageUpload(newFile.size, newFile.type);
    
    // Clean up previous URL and set new one
    setOriginalImageUrl(prevUrl => {
      // Clean up the previous URL if it exists
      if (prevUrl?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(prevUrl);
        } catch (error) {
          console.warn("Failed to revoke previous blob URL:", error);
        }
      }
      return url;
    });
    
    setErrorMessage(null);
    setShowCreditsError(false);
    // Reset result state when new image is uploaded
    setShowResult(false);
    setResultImageUrl(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const handleRender = async () => {
    if (!file) return;
    setLoading(true);
    setProgress(0);
    setErrorMessage(null);
    
    const startTime = Date.now();
    
    // Track generation attempt with enhanced properties
    trackGenerateAttempted({
      prompt: prompt.trim(),
      foundationModel: 'FLUX',
      mode: 'color', // Changed from 'sketch_to_image' to 'color'
      isFinetunedModel: false,
      isImageIncluded: true,
      style: '', // Add style if you have it from presets
    });
    
    try {
      const { userData } = getTokens();
      
      // Always use sketch-to-image mode
      const response = await requestKontextImage({
        image: file,
        prompt: prompt.trim() || undefined,
        sketch_to_image: true, // Always true
        user: userData?.uuid,
      });
      
      if (Array.isArray(response) && response.length > 0) {
        const b64 = response[0].image;
        setResultImageUrl(`data:image/png;base64,${b64}`);
        setShowResult(true);
        setCurrentImageIndex(1); // Show result first on mobile
        
        // Track successful generation with enhanced properties
        const processingTime = Date.now() - startTime;
        trackGenerateSuccess({
          prompt: prompt.trim(),
          fileSize: file.size,
          fileType: file.type,
          processingTimeMs: processingTime,
          foundationModel: 'FLUX',
          mode: 'color', // Changed from 'sketch_to_image' to 'color'
          isFinetunedModel: false,
          isImageIncluded: true,
          style: '', // Add style if you have it from presets
        });

        // Track credit usage (assuming 1 credit per generation)
        trackCreditsDecremented(1, 'generation');
      }
    } catch (e) {
      console.error("Image processing error:", e);
      
      // Enhanced error context for Sentry
      const errorContext = {
        file_size: file?.size,
        file_type: file?.type,
        user_action: "image_processing",
        prompt: prompt,
      };
      
      // Log detailed error information
      if (e && typeof e === 'object' && 'response' in e) {
        const axiosError = e as any;
        console.error("Response status:", axiosError.response?.status);
        console.error("Response data:", axiosError.response?.data);
        
        // Capture in Sentry with rich context
        Sentry.withScope((scope) => {
          scope.setTag("error_area", "image_processing");
          scope.setContext("api_details", {
            status: axiosError.response?.status,
            data: axiosError.response?.data,
            url: axiosError.config?.url,
          });
          scope.setContext("user_context", errorContext);
          Sentry.captureException(e);
        });
        
        // If it's a 400 error, show more specific message
        if (axiosError.response?.status === 400) {
          const errorData = axiosError.response?.data;
          if (errorData?.detail) {
            console.error("API Error Detail:", errorData.detail);
            
            // Check for insufficient credits error
            if (errorData.detail.toLowerCase().includes('insufficient credits') || 
                errorData.detail.toLowerCase().includes('credit') ||
                errorData.detail.toLowerCase().includes('quota')) {
              setErrorMessage("You're out of free credits, but you can purchase more on our iOS app! (Web based purchases coming soon)");
              setShowCreditsError(true);
            } else {
              setErrorMessage(`API Error: ${errorData.detail}`);
              setShowCreditsError(false);
            }
          } else {
            setErrorMessage("Bad request. Please check your input and try again.");
          }
        } else if (axiosError.response?.status === 401) {
          setErrorMessage("Authentication failed. Please log in again.");
        } else if (axiosError.response?.status === 403) {
          setErrorMessage("Access denied. Please check your permissions.");
        } else if (axiosError.response?.status >= 500) {
          setErrorMessage("Server error. Please try again later.");
        } else {
          setErrorMessage("Failed to process image. Please try again.");
        }
      } else if (e && typeof e === 'object' && 'message' in e) {
        console.error("Error message:", (e as Error).message);
        setErrorMessage(`Error: ${(e as Error).message}`);
      } else {
        setErrorMessage("Failed to process image. Please try again.");
      }

      // Track failed generation with iOS event
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      trackGenerateFailed({
        prompt: prompt.trim(),
        errorMessage,
        foundationModel: 'FLUX',
        mode: 'color',
        isFinetunedModel: false,
        isImageIncluded: true,
        style: '', // Add style if you have it from presets
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    // Clean up blob URL to prevent memory leak
    if (originalImageUrl?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(originalImageUrl);
      } catch (error) {
        console.warn("Failed to revoke blob URL in handleGoBack:", error);
      }
    }
    
    // Reset all state to initial values
    setShowResult(false);
    setResultImageUrl(null);
    setCurrentImageIndex(0);
    setFile(null);
    setOriginalImageUrl(null);
    setPrompt("");
    setErrorMessage(null);
    setShowCreditsError(false);
  };

  const handleTryAgain = () => {
    // Keep the original image and file, but reset the result state
    // This allows the user to modify the prompt and re-render
    setShowResult(false);
    setResultImageUrl(null);
    setCurrentImageIndex(0);
    setErrorMessage(null);
    setShowCreditsError(false);
    
    // Recreate blob URL from the original file to ensure it's valid
    if (file) {
      // Clean up the current blob URL first
      if (originalImageUrl?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(originalImageUrl);
        } catch (error) {
          console.warn("Failed to revoke blob URL in handleTryAgain:", error);
        }
      }
      
      // Create a new blob URL from the existing file
      const newUrl = URL.createObjectURL(file);
      setOriginalImageUrl(newUrl);
    }
  };

  const handleExportPNG = () => {
    if (!resultImageUrl) return;
    
    // Track image download event
    // This event is now handled by the new trackImageDownload function
    // trackImageDownload(); 
    
    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = resultImageUrl;
    link.download = 'wand-result.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentImageIndex === 0) {
      // Swipe left from original to result
      setCurrentImageIndex(1);
    }
    if (isRightSwipe && currentImageIndex === 1) {
      // Swipe right from result to original
      setCurrentImageIndex(0);
    }
  };

  const handleDotClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  const dismissError = () => {
    setErrorMessage(null);
    setShowCreditsError(false);
  };

  const handlePromptKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !loading && file) {
      handleRender();
    }
  };

  // Progress bar timer (30s)
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      const totalDuration = 30000;
      const step = 100;
      const intervalTime = totalDuration / step;
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 1;
        });
      }, intervalTime);
    } else {
      setProgress(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading]);

  // Detect touch device capability
  useEffect(() => {
    const checkTouchDevice = () => {
      const hasTouch = (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-ignore - for older browsers
        navigator.msMaxTouchPoints > 0
      );
      setIsTouchDevice(hasTouch);
    };

    checkTouchDevice();
    
    // Listen for touch events to confirm touch capability
    const handleFirstTouch = () => {
      setIsTouchDevice(true);
      window.removeEventListener('touchstart', handleFirstTouch);
    };

    window.addEventListener('touchstart', handleFirstTouch, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleFirstTouch);
    };
  }, []);

  // Clean up on component unmount only
  useEffect(() => {
    return () => {
      if (originalImageUrl?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(originalImageUrl);
        } catch (error) {
          console.warn("Failed to revoke blob URL on unmount:", error);
        }
      }
    };
  }, [originalImageUrl]);

  return (
    <Flex direction="column" align="center" className={pageWrapperStyle}>
      {/* Error Message */}
      {errorMessage && (
        <div className={errorContainerStyle}>
          <div className={errorContentStyle}>
            <span className={errorTextStyle}>{errorMessage}</span>
            <button onClick={dismissError} className={dismissButtonStyle}>
              ×
            </button>
          </div>
          {/* Download App Button for Credits Error */}
          {showCreditsError && (
            <div className={downloadButtonContainerStyle}>
              <a
                href="https://apps.apple.com/us/app/wand-ai-canvas/id1635194640"
                target="_blank"
                rel="noopener noreferrer"
                className={downloadButtonStyle}
              >
                Download Wand App
              </a>
            </div>
          )}
        </div>
      )}

            {/* Image Display Area */}
      {showResult && originalImageUrl && resultImageUrl ? (
        <>
          {/* Desktop: Show both images side by side */}
          <div className={imageComparisonStyle}>
            <div className={imageContainerStyle}>
              <img
                src={originalImageUrl}
                alt="original"
                className={comparisonImageStyle}
                onError={(e) => {
                  console.error("Failed to load original image:", originalImageUrl);
                  console.error("Image error event:", e);
                }}
                onLoad={() => {
                  console.log("Original image loaded successfully:", originalImageUrl);
                }}
              />
              <p className={imageLabelStyle}>Original</p>
            </div>
            <div className={imageContainerStyle}>
              <img
                src={resultImageUrl}
                alt="result"
                className={comparisonImageStyle}
              />
              <p className={imageLabelStyle}>Result</p>
            </div>
          </div>

          {/* Mobile: Show carousel */}
          <div className={mobileCarouselStyle}>
            <div 
              className={carouselContainerStyle}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className={carouselSlideStyle(currentImageIndex)}>
                <div className={slideItemStyle}>
                  <img
                    src={originalImageUrl}
                    alt="original"
                    className={mobileImageStyle}
                    onError={(e) => {
                      console.error("Failed to load original image (mobile):", originalImageUrl);
                      console.error("Mobile image error event:", e);
                    }}
                    onLoad={() => {
                      console.log("Original image loaded successfully (mobile):", originalImageUrl);
                    }}
                  />
                  <p className={imageLabelStyle}>Original</p>
                </div>
                <div className={slideItemStyle}>
                  <img
                    src={resultImageUrl}
                    alt="result"
                    className={mobileImageStyle}
                  />
                  <p className={imageLabelStyle}>Result</p>
                </div>
              </div>
            </div>
            
            {/* Dots indicator */}
            <div className={dotsContainerStyle}>
              <button
                className={dotStyle(currentImageIndex === 0)}
                onClick={() => handleDotClick(0)}
              />
              <button
                className={dotStyle(currentImageIndex === 1)}
                onClick={() => handleDotClick(1)}
              />
            </div>
            
            {/* Swipe hint - only show on touch devices */}
            {isTouchDevice && (
              <p className={swipeHintStyle}>
                Swipe to view {currentImageIndex === 0 ? 'result' : 'original'}
              </p>
            )}
          </div>
        </>
      ) : (
        // Show render interface container
        <div className={renderInterfaceStyle}>
          {/* Drag and drop zone or single image */}
      <div {...getRootProps()} className={dropZoneStyle(isDragActive)}>
        <input {...getInputProps()} />
            {originalImageUrl ? (
          <div className={imageFittedContainerStyle}>
            <img
                  src={originalImageUrl}
              alt="preview"
              className={previewImageStyle}
            />
            <LoadingOverlay isVisible={loading} />
          </div>
        ) : (
          <p className={placeholderTextStyle}>
            Paste or drag image here to colorize
          </p>
        )}
      </div>

      {/* Prompt field */}
      <div className={promptWrapperStyle}>
                  <TextField
            placeholder="Describe your edit (optional)"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handlePromptKeyDown}
          />
      </div>

      {/* Render button */}
          <div className={buttonWrapperStyle}>
        <div className={buttonProgressWrapperStyle}>
          <Button
            onClick={handleRender}
            disabled={!file || loading}
                loading={false}
          >
                {loading ? "Rendering..." : "Render"}
          </Button>
          {loading && (
            <div className={progressBarContainerStyle}>
              <div className={progressBarStyle(progress)} />
            </div>
          )}
        </div>
      </div>
        </div>
      )}

      {/* Result Buttons - only show if showing result */}
      {showResult && (
        <div className={buttonWrapperStyle}>
          <div className={resultButtonsStyle}>
            <div className={css`${resultButtonStyle} ${greyButtonOverrideStyle}`}>
              <Button
                onClick={handleGoBack}
                disabled={false}
                loading={false}
              >
                Start Over
              </Button>
            </div>
            <div className={buttonSpacingStyle} />
            <div className={css`${resultButtonStyle} ${blueButtonOverrideStyle}`}>
              <Button
                onClick={handleTryAgain}
                disabled={false}
                loading={false}
              >
                Try Again
              </Button>
            </div>
            <div className={buttonSpacingStyle} />
            <div className={resultButtonStyle}>
              <Button
                onClick={handleExportPNG}
                disabled={false}
                loading={false}
              >
                Export Result
              </Button>
            </div>
          </div>
        </div>
      )}
    </Flex>
  );
};

const pageWrapperStyle = css`
  box-sizing: border-box;
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 40px 16px 40px;
  min-height: calc(100vh - 100px); /* Account for header height */
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 16px;
    justify-content: flex-start;
    min-height: auto; /* Remove min-height constraint */
  }
`;

const renderInterfaceStyle = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    background-color: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    padding: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    flex: 1;
    display: flex;
    justify-content: flex-start; /* Changed from center to flex-start */
  }
`;

const dropZoneStyle = (isActive: boolean) => css`
  width: 100%;
  max-width: 600px;
  height: 450px;
  border: 2px dashed rgba(170, 170, 170, 0.5);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  ${isActive ? `background-color: rgba(15, 125, 255, 0.1);` : ""}

  @media (max-width: 768px) {
    height: calc(100vh - 400px); /* Account for header, padding, prompt, button, footer, and margins */
    max-height: none;
    min-height: 250px;
    max-width: 550px;
  }
`;

const placeholderTextStyle = css`
  text-align: center;
  color: #aaaaaa;
  font-size: 18px;
`;

const previewImageStyle = css`
  display: block;
  max-width: 100%;
  max-height: 450px;
  object-fit: contain;
  border-radius: 12px;

  @media (max-width: 768px) {
    max-height: calc(100vh - 400px);
    max-width: 100%;
  }
`;

const imageFittedContainerStyle = css`
  position: relative;
  display: inline-block;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  max-height: 100%;
`;

const promptWrapperStyle = css`
  margin-top: 24px;
  width: 100%;
  max-width: 600px;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    margin-top: 20px;
    flex-shrink: 0;
    max-width: 100%;
  }
`;

const buttonWrapperStyle = css`
  margin-top: 40px;
  width: 100%;
  max-width: 600px; /* Match dropzone desktop width */
  display: flex;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    margin-top: 24px;
    max-width: 550px; /* Match TextField max-width */
  }
`;

const buttonProgressWrapperStyle = css`
  position: relative;
  width: 100%;
  max-width: 600px;
  
  & button {
    border-radius: 50px !important; /* Full pill shape */
    width: 100% !important;
    max-width: none !important;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const progressBarContainerStyle = css`
  position: absolute;
  bottom: -8px;
  left: 0;
  right: 0;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
`;

const progressBarStyle = (progress: number) => css`
  height: 100%;
  background-color: #0f7dff;
  border-radius: 2px;
  transition: width 0.1s ease;
  width: ${progress}%;
`;

// New styles for result view
const imageComparisonStyle = css`
  display: flex;
  gap: 48px;
  width: 100%;
  max-width: 1200px;
  justify-content: center;
  align-items: flex-start;
  flex: 1;
  min-height: 0;
  padding: 20px 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const imageContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-height: 0;
  max-height: 100%;
`;

const comparisonImageStyle = css`
  width: 100%;
  height: 100%;
  max-width: 600px;
  max-height: calc(100vh - 300px); /* Reserve space for header, labels, and buttons */
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  @media (max-width: 1024px) {
    max-height: calc(100vh - 280px);
  }

  @media (max-width: 768px) {
    max-width: 250px;
    max-height: 250px;
  }
`;

const imageLabelStyle = css`
  margin-top: 12px;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
`;

const resultButtonsStyle = css`
  display: flex;
  gap: 24px;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    max-width: 320px;
    margin: 0 auto;
    padding-top: 16px;
  }
`;

const resultButtonStyle = css`
  flex: 1;
  
  & button {
    width: 100% !important;
    max-width: none !important;
    border-radius: 50px !important; /* Full pill shape */
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const greyButtonOverrideStyle = css`
  & button {
    background-color: #666666 !important;
    
    &:hover {
      background-color: #777777 !important;
    }
    
    &:active {
      background-color: #555555 !important;
    }
  }
`;

const buttonSpacingStyle = css`
  width: 16px;

  @media (max-width: 768px) {
    width: 0;
    height: 0;
  }
`;

// Mobile carousel styles
const mobileCarouselStyle = css`
  display: none;
  width: 100%;

  @media (max-width: 768px) {
    display: block;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 0;
  }
`;

const carouselContainerStyle = css`
  overflow: hidden;
  width: 100%;
  border-radius: 12px;
`;

const carouselSlideStyle = (currentIndex: number) => css`
  display: flex;
  width: 200%;
  transform: translateX(-${currentIndex * 50}%);
  transition: transform 0.3s ease-in-out;
`;

const slideItemStyle = css`
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 8px;
`;

const mobileImageStyle = css`
  width: 100%;
  max-width: 320px;
  height: auto;
  max-height: 60vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const dotsContainerStyle = css`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
`;

const dotStyle = (isActive: boolean) => css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background-color: ${isActive ? '#0f7dff' : '#666666'};
  cursor: pointer;
  transition: background-color 0.2s ease;
`;

const swipeHintStyle = css`
  text-align: center;
  color: #888888;
  font-size: 12px;
  margin-top: 8px;
`;

// Error message styles
const errorContainerStyle = css`
  width: 100%;
  margin-bottom: 20px;
  animation: slideDown 0.3s ease-out;
  flex-shrink: 0;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const errorContentStyle = css`
  background-color: #ff4444;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(255, 68, 68, 0.3);
`;

const errorTextStyle = css`
  font-size: 14px;
  font-weight: 500;
`;

const dismissButtonStyle = css`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const downloadButtonContainerStyle = css`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`;

const downloadButtonStyle = css`
  display: inline-block;
  background: #0f7dff;
  color: white;
  text-decoration: none;
  padding: 16px 32px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  transition: background-color 0.3s ease, transform 0.2s ease;
  box-shadow: 0 4px 12px rgba(15, 125, 255, 0.3);

  &:hover {
    background-color: #449bff;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(15, 125, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const blueButtonOverrideStyle = css`
  & button {
    background-color: #0f7dff !important;
    
    &:hover {
      background-color: #449bff !important;
    }
    
    &:active {
      background-color: #0a6de6 !important;
    }
  }
`;

export default ColorPage; 